import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { 
  Box, 
  CircularProgress,   
  IconButton,   
  Menu,   
  MenuItem,   
  Switch,   
  Table, 
  TableBody, 
  TableCell, 
  TableFooter, 
  TableHead, 
  TablePagination, 
  TableRow, 
  TableSortLabel, 
  Typography 
} from '@mui/material';

import TablePaginationActions from '../../../components/utils/TablePaginationActions';
import useTablePaginationInit from '../../../components/hooks/useTablePaginationInit';
import TableTimeStamps from '../../../components/utils/TableTimeStamps';
import Copyable from '../../../components/utils/Copyable';
import TopBar from '../../../components/utils/TopBar';
import NA from '../../../components/utils/NA';

import MoreVertIcon from '@mui/icons-material/MoreVert';
import DataObjectIcon from '@mui/icons-material/DataObject';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteIcon from '@mui/icons-material/Delete';

import { 
  CreateSystemConfigBIZ,
  DeleteSystemConfigBIZ,
  GetSystemConfigBIZ,
  ToggleSystemConfigBIZ
} from '../../../redux/actions/business/systemConfigActions';

import ConfirmationModal from '../../../components/modals/ConfirmationModal';
import SystemConfigModal from './SystemConfigModal';

import global from "../../../global.scss";

const SystemConfigPage = () => {
  const dispatch = useDispatch();
  const state = useSelector(state => state.business);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");
  const [deleteConfirmationModalOpen, setDeleteConfirmationModalOpen] = useState(false);
  const [systemConfigModalOpen, setSystemConfigModalOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [update, setUpdate] = useState(null);
  
  const [systemConfigList, setSystemConfigList] = useState([]);
  const [systemConfigTotal, setSystemConfigTotal] = useState(0);

  const [systemConfigMenuOpen, setSystemConfigMenuOpen] = useState(false);
  const [systemConfigMenuAnchor, setSystemConfigMenuAnchor] = useState(null);

  const [deleteId, setDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  
  const [
    page, 
    rowsPerPage, 
    handleChangePage, 
    handleChangeRowsPerPage
  ] = useTablePaginationInit();

  const tableHeadingsList = [
    {
      label: "Id",
      sort: "id",
    },
    {
      label: "json",
      sort: null,
    },
    {
      label: "Description",
      sort: "description",
    },
    {
      label: "Active",
      sort: "is_active",
    },
    {
      // be-core's model column is `created_at`, not the `createdAt` used by
      // the be-identity (Sequelize) tables.
      label: "Time stamps",
      sort: "created_at",
    },
    {
      label: "",
      sort: null,
    },
  ];

  const handleSort = (value) => {
    setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    setSort(value);
  };

  useEffect(() => {
    handleGetSystemConfig();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, search, sort, sortDirection]);

  useEffect(() => {
    try {
      formatSystemConfigList(state.systemConfigList.records || []);
      setSystemConfigTotal(state.systemConfigList.total || 0);
    } catch (err) {}
  }, [state]);

  const formatSystemConfigList = (list) => {
    setSystemConfigList(list);
  };

  async function handleGetSystemConfig(customLoading){
    let query = ""
    if(search) query += `&search=${search}`;
    // be-core is Django/DRF — its OrderingFilter reads `ordering=field` /
    // `ordering=-field`. The `field`/`order` pair used by the be-identity
    // controllers is ignored here, which is why sorting did nothing.
    if(sort) query += `&ordering=${sortDirection === "asc" ? '' : '-'}${sort}`;
    await dispatch(GetSystemConfigBIZ(page+1, rowsPerPage, query, customLoading ? customLoading : setLoading));
  };

  const handleCreateSystemConfig = () => {
    try { 
      let obj = {
        "json": JSON.stringify({
          "version": "1.0",
          "name": "",
          "description": "",
          "app_config": {
            "be": {
              "identity": {},
              "core": {},
              "fusion": {},
              "webhook": {},
              "accounting": {
                "qbd": {
                  "fetch_from_qbd": {
                    "class": true, 
                    "account": true,
                    "sales_tax": true, 
                    "item": true, 
                    "vendor": true, 
                    "customer": true, 
                    "bill": true, 
                    "bill_payment": true, 
                    "invoice": true,
                    "invoice_payment": true,
                    "journal_entry": true,
                    "check": true
                  }
                },
                "qbo": {},
                "xero": {}
              }
            },
            "fe": {}
          }
        })
      };
      dispatch(CreateSystemConfigBIZ(obj, setLoading)).then(() => {
        handleGetSystemConfig(setLoading);
      });
    } catch (err) {
      console.log(err);
    }; 
  }; 

  const handleToggleSystemConfig = (id) => {
    try {
      dispatch(ToggleSystemConfigBIZ(id, setLoading)).then(() => {
        handleGetSystemConfig(setLoading);
      });
    } catch (err) {
      console.log(err);
    }
  };
  
  const handleDelete = () => {
    setDeleteConfirmationModalOpen(false);
    dispatch(DeleteSystemConfigBIZ(deleteId, setDeleting)).then(() => {
      handleGetSystemConfig().then(() => {
        setDeleteId(null);
      })
    });
  }

  return (
    <Box>

      <SystemConfigModal 
        open={systemConfigModalOpen}
        setOpen={setSystemConfigModalOpen}
        title={modalTitle}
        update={update}
        handleGet={handleGetSystemConfig}
        />

      <ConfirmationModal 
        open={deleteConfirmationModalOpen}
        setOpen={setDeleteConfirmationModalOpen}
        title={"Delete " + modalTitle}
        onCancel={() => setDeleteId(null)}
        onConfirm={handleDelete}
        />

      <TopBar
        title={"System Configuration - GLOBAL"}
        search={search}
        onSearchChange={(e) => {
          setSearch(e.target.value);
        }}
        handleCreateSystemConfig
        button1Text="Create new configuration"
        button1OnClick={() => handleCreateSystemConfig()}
        />
      
      {
        loading
        ?
        <Box height={"300px"} className="flexCenterCenterRow">
          <CircularProgress />
        </Box>
        :
          systemConfigList.length === 0
          ?
          <Box height={"300px"} className="flexCenterCenterRow">
            <Typography>No system config found</Typography>
          </Box>
          :
          <Table className="block">
            <TableHead>
              <TableRow>
                {
                  tableHeadingsList.map((heading, headingIndex) => (
                    heading.sort === null
                    ?
                    <TableCell
                      key={headingIndex}>{heading.label}</TableCell>
                    :
                    <TableCell
                      key={headingIndex}>
                      <TableSortLabel
                        active={sort === heading.sort ? true : false}
                        direction={sortDirection}
                        onClick={() => {
                          handleSort(heading.sort);
                        }}
                      >
                        {heading.label}
                      </TableSortLabel>
                    </TableCell>
                  ))
                }
              </TableRow>
            </TableHead>
            <TableBody>
              {
                systemConfigList.map((row, index) => (
                  <TableRow 
                    key={index}
                    className="table__clickable-row">
                    <TableCell>
                      <Typography
                        variant='caption'
                        color={"grey"}>
                        <Copyable>
                          {row.id || <NA />}
                        </Copyable>
                      </Typography>
                    </TableCell>
                    <TableCell>{"JSON" || <NA />}</TableCell>
                    <TableCell>{row.description || <NA />}</TableCell>
                    <TableCell>
                      <Switch
                        checked={row.is_active}
                        onChange={(e) => {
                          handleToggleSystemConfig(row.id);
                        }}
                        />
                    </TableCell>   
                    <TableCell>
                      <TableTimeStamps
                        createdAt={row.created_at}
                        updatedAt={row.updated_at}
                        />
                    </TableCell>
                    <TableCell>
                      {
                        row.id === deleteId && deleting
                        ?
                        <CircularProgress size={26} sx={{ margin: "7px" }}/>
                        :
                        <IconButton
                          onClick={(e) => {
                            setSystemConfigMenuAnchor(e.currentTarget);
                            setSystemConfigMenuOpen(true);
                            setUpdate(row);
                            setModalTitle(row.id || "");
                            setDeleteId(row.id);
                          }}>
                          <MoreVertIcon sx={{ color: global['secondary_light']}}/>
                        </IconButton>
                      }
                    </TableCell>
                  </TableRow>
                ))
              }
            </TableBody>
            <TableFooter>
              <TableRow>
                <TablePagination
                  sx={{ borderBottom: 0 }}
                  rowsPerPageOptions={[5, 10, 25, 50, 100]} 
                  colSpan={10}
                  count={systemConfigTotal}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  SelectProps={{
                    inputProps: {
                      'aria-label': 'rows per page'
                    },
                    native: true
                  }}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  ActionsComponent={TablePaginationActions}
                  />
              </TableRow>
            </TableFooter>
          </Table>
      }
      <Menu
        open={systemConfigMenuOpen}
        anchorEl={systemConfigMenuAnchor}
        onClose={() => setSystemConfigMenuOpen(false)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem
          onClick={() => {
            setModalTitle("Edit config — " + modalTitle);
            setUpdate({
              ...update,
              defaultMode: 2
            });
            setSystemConfigMenuOpen(false);
            setSystemConfigModalOpen(true);
          }}>
          <Box
            className="flexCenter_Row"
            gap={"10px"}>
            <DataObjectIcon />
            Edit config (JSON)
          </Box>
        </MenuItem>
        <MenuItem
          onClick={() => {
            setModalTitle("Edit description — " + modalTitle);
            setUpdate({
              ...update,
              defaultMode: 3
            });
            setSystemConfigMenuOpen(false);
            setSystemConfigModalOpen(true);
          }}>
          <Box
            className="flexCenter_Row"
            gap={"10px"}>
            <EditOutlinedIcon />
            Edit description
          </Box>
        </MenuItem>
        {
          update?.is_active === false
          &&
          <MenuItem
            onClick={() => {
              setSystemConfigMenuOpen(false);
              setDeleteConfirmationModalOpen(true);
            }}>
            <Box
              className="flexCenter_Row"
              gap={"10px"}>
              <DeleteIcon />
              Delete
            </Box>
          </MenuItem>
        }
      </Menu>
    </Box>
  )
}

export default SystemConfigPage;